import {
  ReactiveFormsModule,
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators
} from '@angular/forms';
import { Component, OnInit } from '@angular/core';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { ButtonDirective } from 'primeng/button';
import { Checkbox } from 'primeng/checkbox';
import { InputNumber } from 'primeng/inputnumber';
import { InputText } from 'primeng/inputtext';
import { Textarea } from 'primeng/textarea';
import { CalendarInputComponent } from '../../../shared/calendar-input/calendar-input.component';
import { AppointmentPeriod } from '../../../../models/AppointmentPeriod';
import { AppointmentPeriodService } from '../../../../services/appointment-period.service';
import { UtilsService } from '../../../../services/utils.service';
import { UserService } from 'src/app/services/user.service';
import { SelectModule } from 'primeng/select';
import { ProductPackage } from 'src/app/models/ProductPackage';
import { ProductService } from 'src/app/services/product.service';
import { SettingDropdownComponent } from '../../../shared/setting-dropdown/setting-dropdown.component';
import { Setting } from 'src/app/models/Setting';
import { SettingService } from 'src/app/services/setting.service';
import { ConfirmationService, MessageService } from 'primeng/api';

interface WeekdayDef {
  key: string;
  label: string;
  jsDay: number;
}

type PeriodDialogData = Partial<AppointmentPeriod> & {
  periodStart?: Date | string;
  periodEnd?: Date | string;
};

@Component({
  selector: 'app-appointment-period-dialog',
  imports: [
    ReactiveFormsModule,
    TranslatePipe,
    CalendarInputComponent,
    Checkbox,
    InputNumber,
    InputText,
    Textarea,
    ButtonDirective,
    SelectModule,
    SettingDropdownComponent
  ],
  templateUrl: './appointment-period-dialog.component.html',
  styleUrls: ['./appointment-period-dialog.component.scss']
})
export class AppointmentPeriodDialogComponent implements OnInit {
  public form: UntypedFormGroup;
  public saving = false;
  public users: Array<any> | null = null;
  public userOptions: Array<any> = [];
  public selectedUser: any = null;
  public productPackages: Array<ProductPackage> | null = null;
  public filteredProductPackages: Array<ProductPackage> | null = null;
  public packageUserTypes: Array<Setting> | null = null;
  public filterType: string = 'service';
  public readonly isEdit: boolean;
  public readonly weekdays: Array<WeekdayDef> = [
    { key: 'mon', label: 'appointment-period.weekday.mon', jsDay: 1 },
    { key: 'tue', label: 'appointment-period.weekday.tue', jsDay: 2 },
    { key: 'wed', label: 'appointment-period.weekday.wed', jsDay: 3 },
    { key: 'thu', label: 'appointment-period.weekday.thu', jsDay: 4 },
    { key: 'fri', label: 'appointment-period.weekday.fri', jsDay: 5 },
    { key: 'sat', label: 'appointment-period.weekday.sat', jsDay: 6 },
    { key: 'sun', label: 'appointment-period.weekday.sun', jsDay: 0 }
  ];

  private readonly existingPeriod: PeriodDialogData | null;

  constructor(
    private ref: DynamicDialogRef,
    private config: DynamicDialogConfig,
    private formBuilder: UntypedFormBuilder,
    private appointmentPeriodService: AppointmentPeriodService,
    private translateService: TranslateService,
    private utilsService: UtilsService,
    private userService: UserService,
    private productService: ProductService,
    private settingService: SettingService,
    private messageService: MessageService
  ) {
    const weekdayGroup: Record<string, unknown[]> = {};
    this.weekdays.forEach(
      (weekday) => (weekdayGroup[weekday.key] = [weekday.jsDay >= 1 && weekday.jsDay <= 5])
    );

    this.form = this.formBuilder.group({
      id: [''],
      startDate: [null, Validators.required],
      endDate: [null, Validators.required],
      weekdays: this.formBuilder.group(weekdayGroup),
      startTime: ['08:00', Validators.required],
      endTime: ['16:00', Validators.required],
      eventTime: [''],
      suggestionTime: ['', [Validators.required]],
      break: [''],
      breaks: this.formBuilder.array([]),
      userId: ['', Validators.required],
      productPackageId: [''],
      productPackageUserType: ['', Validators.required],
      speciality: [''],
      info: ['']
    });

    this.existingPeriod = (this.config.data?.profileObject as PeriodDialogData) || null;
    this.isEdit = !!this.existingPeriod?.id;
  }

  private getUsers(): void {
    this.userService.getScheduleUsers().subscribe((result) => {
      this.users = result;
      this.userOptions = this.users!.map((user) => {
        return {
          id: user.id,
          title: `${user.lastName} ${user.firstName}`,
          sortOrder: `${user.lastName}`
        };
      });

      // Set the default selected user
      this.selectedUser = this.userOptions.find(
        (user) => user.id === this.form.get('userId')?.value
      );
    });
  }

  private getProductPackages(): void {
    this.productService.getPackagesByType('schedule', true).then((packages) => {
      this.productPackages = packages;

      if (this.existingPeriod && this.existingPeriod.productPackageUserType) {
        this.filteredProductPackages = this.productPackages.filter(
          (pkg) => pkg.userType === this.existingPeriod!.productPackageUserType
        );
      }
    });

    this.settingService
      .getSettingByName('PACKAGE_USER_TYPE')
      .then((settings) => {
        this.packageUserTypes = settings;
      });
  }

  ngOnInit(): void {
    const data = this.config.data || {};
    this.filterType = this.config.data?.filterType || 'service';
    const period = this.existingPeriod;
    const start = this.asDate(
      period?.startDate || period?.['periodStart'] || data.defaultStartDate
    );
    const end = this.asDate(
      period?.endDate || period?.['periodEnd'] || data.defaultEndDate || start
    );

    this.getUsers();
    this.getProductPackages();

    this.form.patchValue({
      id: period?.id || '',
      startDate: start,
      endDate: end,
      startTime:
        period?.startTime || this.timeFromDate(period?.['periodStart']) || data.defaultStartTime || '08:00',
      endTime:
        period?.endTime || this.timeFromDate(period?.['periodEnd']) || data.defaultEndTime || '16:00',
      eventTime: period?.eventTime ?? '',
      suggestionTime: period?.suggestionTime ?? '00:30:00',
      break: period?.break ?? '',
      userId: period?.userId || data.userId || '',
      productPackageId: period?.productPackageId || '',
      productPackageUserType: period?.productPackageUserType || '',
      speciality: period?.speciality || '',
      info: period?.info || ''
    });

    this.applyTimeToDate('startDate', 'startTime');
    this.applyTimeToDate('endDate', 'endTime');
    this.form.get('startDate')?.valueChanges.subscribe(() =>
      this.applyTimeToDate('startDate', 'startTime')
    );
    this.form.get('endDate')?.valueChanges.subscribe(() =>
      this.applyTimeToDate('endDate', 'endTime')
    );

    const weekdayGroup = this.form.get('weekdays') as UntypedFormGroup;
    let selectedDays = this.selectedDays(period);
    // New period on a weekend day: preselect only that day instead of Mon-Fri.
    if (!selectedDays && (data.onlyJsDay === 0 || data.onlyJsDay === 6)) {
      selectedDays = [data.onlyJsDay];
    }
    this.weekdays.forEach((weekday) => {
      weekdayGroup.get(weekday.key)?.setValue(
        selectedDays
          ? selectedDays.includes(weekday.jsDay)
          : weekday.jsDay >= 1 && weekday.jsDay <= 5
      );
    });
  }

  public doClose(): void {
    this.ref.close(null);
  }

  get breaks(): UntypedFormArray {
    return this.form.get('breaks') as UntypedFormArray;
  }

  public addBreak(): void {
    this.breaks.push(
      this.formBuilder.group({
        startTime: ['12:00', Validators.required],
        durationMinutes: [30, [Validators.required, Validators.min(1)]]
      })
    );
  }

  public removeBreak(index: number): void {
    this.breaks.removeAt(index);
  }

  public save(): void {
    const rows = this.buildRows();
    if (typeof rows === 'string') {
      this.utilsService.showError(this.translateService.instant(rows));
      return;
    }
    this.saving = true;

    if (this.isEdit) {
      this.appointmentPeriodService.saveAppointmentPeriod(rows[0]).subscribe({
        next: (data) => {
          this.saving = false;
          this.ref.close({ type: 'updatePeriod', data });
          this.messageService.add({
            severity: 'success',
            summary: 'Save Confirmation',
            detail: 'Saved successfully'
          });
        },
        error: (error: any) => this.handleError(error)
      });
      return;
    } else {
      this.appointmentPeriodService.saveAppointmentPeriods(rows).subscribe({
        next: (data) => {
          this.saving = false;
          this.ref.close({
            type: 'batchAdded',
            added: data?.length ?? rows.length,
            replaced: 0
          });
        },
        error: (error: any) => this.handleError(error)
      });
    }
  }

  public onDelete(): void {
    if (!this.existingPeriod?.id) {
      return;
    }
    // TODO: Confirm deletion with a dialog before proceeding.
    this.saving = true;
    this.appointmentPeriodService.deleteAppointmentPeriod(this.existingPeriod.id).subscribe({
      next: () => {
        this.saving = false;
        this.ref.close({ type: 'delPeriod', data: this.existingPeriod });
        this.messageService.add({
          severity: 'success',
          summary: 'Delete Confirmation',
          detail: 'Deleted successfully'
        });
      },
      error: (error: any) => {
        this.saving = false;
        this.handleError(error);
      }
    });
  }

  public onPackageUserTypeSelect(userType: string): void {
    if (userType) {
      this.filteredProductPackages = this.productPackages?.filter(pkg => pkg.userType === userType) || null;
    } else {
      this.filteredProductPackages = null;
    }
  }

  public onPackageSelect(packageId: string): void {
    /*
    const productPackage = this.productPackages?.find(pkg => pkg.id === packageId);
    if (productPackage) {
      this.form.patchValue({
        eventTime: productPackage.duration
      });
    }
      */
  }

  private buildRows(): Array<AppointmentPeriod> | string {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return 'appointment-period.invalid-form';
    }

    const value = this.form.getRawValue();
    const startDate = value.startDate; // this.dateOnly(value.startDate);
    const endDate = value.endDate; // this.dateOnly(value.endDate);
    const startMinutes = this.timeToMinutes(value.startTime);
    const endMinutes = this.timeToMinutes(value.endTime);
    const days = this.weekdays
      .filter((weekday) => value.weekdays[weekday.key])
      .map((weekday) => weekday.jsDay);

    if (!startDate || !endDate || endDate < startDate) {
      return 'appointment-period.invalid-date-range';
    }
    if (startMinutes === null || endMinutes === null || endMinutes <= startMinutes) {
      return 'appointment-period.invalid-time-range';
    }
    if (!days.length) {
      return 'appointment-period.no-days-selected';
    }

    // Fields shared by every period produced from this form. A break only
    // changes startTime/endTime, so these stay identical across the split.
    const base = {
      ...(this.existingPeriod || {}),
      userId: value.userId,
      startDate,
      endDate,
      days,
      weekdays: days.join(','),
      eventTime: value.eventTime,
      suggestionTime: value.suggestionTime,
      break: value.break,
      productPackageId: value.productPackageId || undefined,
      productPackageUserType: value.productPackageUserType || undefined,
      speciality: value.speciality || undefined,
      info: value.info || undefined
    } as AppointmentPeriod;

    // Editing an existing period never splits: keep a single row.
    if (this.isEdit) {
      return [
        {
          ...base,
          id: this.existingPeriod?.id || '',
          startTime: value.startTime,
          endTime: value.endTime
        }
      ];
    }

    // Each break splits the time range into one more presence segment; every
    // segment becomes its own period spanning the same dates and weekdays.
    const segments = this.splitTimeRange(startMinutes, endMinutes, value.breaks || []);
    if (typeof segments === 'string') {
      return segments;
    }

    return segments.map((segment) => ({
      ...base,
      id: '',
      startTime: this.minutesToTime(segment.start),
      endTime: this.minutesToTime(segment.end)
    }));
  }

  /**
   * Split [startMin, endMin] around the given breaks into presence segments.
   * Returns an i18n error key instead of segments when a break is invalid.
   */
  private splitTimeRange(
    startMin: number,
    endMin: number,
    breaks: Array<any>
  ): Array<{ start: number; end: number }> | string {
    const parsed = breaks.map((b) => {
      const start = this.timeToMinutes(b.startTime);
      const duration = Number(b.durationMinutes);
      return { start, end: start === null ? null : start + duration, duration };
    });

    for (const b of parsed) {
      if (b.start === null || !(b.duration > 0)) {
        return 'appointment-period.invalid-break';
      }
      if (b.start < startMin || (b.end as number) > endMin) {
        return 'appointment-period.break-outside-range';
      }
    }

    parsed.sort((a, b) => (a.start as number) - (b.start as number));
    for (let i = 1; i < parsed.length; i++) {
      if ((parsed[i].start as number) < (parsed[i - 1].end as number)) {
        return 'appointment-period.breaks-overlap';
      }
    }

    const segments: Array<{ start: number; end: number }> = [];
    let cursor = startMin;
    for (const b of parsed) {
      if ((b.start as number) > cursor) {
        segments.push({ start: cursor, end: b.start as number });
      }
      cursor = Math.max(cursor, b.end as number);
    }
    if (endMin > cursor) {
      segments.push({ start: cursor, end: endMin });
    }
    return segments;
  }

  private selectedDays(period: PeriodDialogData | null): Array<number> | null {
    if (!period) {
      return null;
    }
    if (Array.isArray(period.days)) {
      return period.days;
    }
    if (period.weekdays) {
      return period.weekdays
        .split(',')
        .map((day) => Number(day.trim()))
        .filter((day) => !isNaN(day));
    }
    const date = this.asDate(period.startDate || period['periodStart']);
    return date ? [date.getDay()] : null;
  }

  private handleError(error: any): void {
    this.saving = false;
    this.utilsService.showError(error?.error?.message || error?.message || 'Save failed');
  }

  private dateOnly(value: any): Date | null {
    const date = this.asDate(value);
    return date ? new Date(date.getFullYear(), date.getMonth(), date.getDate()) : null;
  }

  private asDate(value: any): Date | null {
    if (!value) {
      return null;
    }
    const date = value instanceof Date ? new Date(value) : new Date(value);
    return isNaN(date.getTime()) ? null : date;
  }

  private timeFromDate(value: any): string | null {
    const date = this.asDate(value);
    return date
      ? `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`
      : null;
  }

  private timeToMinutes(value: string): number | null {
    if (!value || !/^\d{2}:\d{2}$/.test(value)) {
      return null;
    }
    const [hours, minutes] = value.split(':').map(Number);
    return hours <= 23 && minutes <= 59 ? hours * 60 + minutes : null;
  }

  private minutesToTime(minutes: number): string {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}`;
  }

  onTimeSelect(_event: Event, key: 'startTime' | 'endTime'): void {
    this.applyTimeToDate(key === 'startTime' ? 'startDate' : 'endDate', key);
  }

  private applyTimeToDate(
    dateKey: 'startDate' | 'endDate',
    timeKey: 'startTime' | 'endTime'
  ): void {
    const minutes = this.timeToMinutes(this.form.get(timeKey)?.value);
    const date = this.asDate(this.form.get(dateKey)?.value);
    if (minutes === null || !date) {
      return;
    }
    date.setHours(Math.floor(minutes / 60), minutes % 60, 0, 0);
    // emitEvent: false avoids re-triggering the date valueChanges subscription.
    this.form.get(dateKey)?.setValue(date, { emitEvent: false });
  }
}
